/**
 * PrivacyPolicyPage.tsx
 *
 * TeachBud Privacy Policy — effective date: 2026-08-03
 * IMPORTANT: This document is a draft for internal review and must be reviewed
 * by a qualified attorney before TeachBud opens to the general public.
 */

import React from 'react';
import { ArrowLeft, Shield } from 'lucide-react';

interface PrivacyPolicyPageProps {
  onBack?: () => void;
}

export function PrivacyPolicyPage({ onBack }: PrivacyPolicyPageProps) {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          {onBack && (
            <button
              onClick={onBack}
              className="h-8 w-8 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100
                flex items-center justify-center transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              aria-label="Go back"
            >
              <ArrowLeft size={16} />
            </button>
          )}
          <div className="flex items-center gap-2">
            <Shield size={16} className="text-primary" />
            <span className="text-sm font-bold text-slate-900">TeachBud Privacy Policy</span>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-3xl mx-auto px-6 py-10 space-y-8 text-slate-700 text-sm leading-relaxed">

        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 text-amber-800 text-xs">
          <strong>Attorney Review Notice:</strong> This Privacy Policy is a draft prepared for the private beta phase of TeachBud.
          It has not yet been reviewed by a qualified attorney. Do not rely on it for legal compliance purposes, including FERPA,
          COPPA, or state student-privacy laws, until a formal legal review has been completed.
        </div>

        <div>
          <p className="text-xs text-slate-400 mb-2">Last updated: August 3, 2026 · Effective: August 3, 2026</p>
          <h1 className="text-2xl font-bold text-slate-900 mb-3">Privacy Policy</h1>
          <p>
            This Privacy Policy describes how TeachBud ("we," "us," or "our") collects, uses, and shares information
            about you when you use the TeachBud web application (the "Service"). We are committed to protecting the
            privacy of the educators who use our platform.
          </p>
        </div>

        <Section title="1. Who We Are">
          <p>
            TeachBud is an AI-powered lesson-material generator designed for K–8 classroom teachers.
            We help educators create lesson plans, activity sheets, assessments, and presentations quickly and
            consistently. We are currently operating in a private beta and are not yet open to the general public.
          </p>
          <p className="mt-2">
            For questions about this policy, contact us at{' '}
            <a href="mailto:support@teachbud.app" className="text-primary underline hover:no-underline">support@teachbud.app</a>.
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <SubSection title="2a. Account and Profile Information">
            <p>
              When you create an account, we collect your name, email address, and the credentials necessary to sign
              in through our authentication provider (Clerk). You may also provide additional profile information such
              as your school name, school district, grade levels you teach, subjects, and instructional preferences.
              This information is stored in our database and used to personalize the Service.
            </p>
          </SubSection>
          <SubSection title="2b. Curriculum Files">
            <p>
              You may upload documents (PDF, Word, PowerPoint) or provide URLs to curriculum documents.
              These files are processed to extract instructional metadata (topics, standards, learning objectives)
              and then used to generate lesson materials. Uploaded file content is stored on our servers only as
              long as necessary to support the Curriculum Hub feature and may be deleted when you remove a curriculum
              entry from your account.
            </p>
          </SubSection>
          <SubSection title="2c. Generated Resources">
            <p>
              Lessons, activity sheets, assessments, and presentations you generate are stored in your account so
              you can retrieve them later. These resources are associated with your user ID and are not shared with
              other users.
            </p>
          </SubSection>
          <SubSection title="2d. Usage Information">
            <p>
              We collect information about how you use the Service, including which features you use, when you
              generate resources, and which settings you choose. This data helps us improve the product.
            </p>
          </SubSection>
          <SubSection title="2e. Feedback and Support Requests">
            <p>
              If you submit feedback through the Help & Feedback panel, we store your category, message, the page
              you were on, optional screenshots, and your account details alongside the submission.
            </p>
          </SubSection>
          <SubSection title="2f. Technical Information">
            <p>
              We automatically collect browser type, operating system, screen dimensions, IP address, and other
              standard request metadata. We use session cookies and browser storage to keep you signed in and to
              remember your preferences.
            </p>
          </SubSection>
        </Section>

        <Section title="3. How We Use Your Information">
          <ul className="list-disc list-inside space-y-1.5 ml-2">
            <li>To authenticate your account and maintain your session.</li>
            <li>To generate lesson materials tailored to your settings.</li>
            <li>To save your resources and curriculum files to your account.</li>
            <li>To send transactional emails (password reset, account notices) through our authentication provider.</li>
            <li>To respond to feedback and support requests you submit.</li>
            <li>To improve the accuracy and quality of AI-generated content.</li>
            <li>To detect and prevent abuse, unauthorized access, and security incidents.</li>
            <li>To analyze how the Service is used in aggregate (no individual profiling for advertising).</li>
          </ul>
          <p className="mt-3">
            We do not sell your personal information. We do not use your data to serve you advertising.
          </p>
        </Section>

        <Section title="4. AI Processing">
          <p>
            TeachBud uses OpenAI's API to generate lesson content, assess curriculum documents, and produce
            assessments and presentations. When you generate a resource, the relevant inputs (skill description,
            grade level, subject, curriculum text excerpts) are sent to OpenAI's servers for processing.
          </p>
          <p className="mt-2">
            We do not intentionally transmit personally identifiable student information to OpenAI. You are
            responsible for ensuring that any content you include in your inputs (curriculum files, lesson topics,
            or instructions) does not contain student PII.
          </p>
          <p className="mt-2">
            OpenAI's use of data submitted through the API is governed by{' '}
            <a href="https://openai.com/policies/api-data-usage-policies" target="_blank" rel="noopener noreferrer"
              className="text-primary underline hover:no-underline">OpenAI's API data usage policies</a>.
            OpenAI does not use API input/output to train models unless you opt in.
          </p>
        </Section>

        <Section title="5. Cookies and Local Storage">
          <p>
            We use cookies and browser local storage for the following purposes:
          </p>
          <ul className="list-disc list-inside space-y-1.5 ml-2 mt-2">
            <li><strong>Authentication cookies:</strong> managed by Clerk to keep you signed in securely.</li>
            <li><strong>Preference storage:</strong> your theme choices, default settings, and onboarding status are stored in your browser's local storage so they persist across sessions.</li>
            <li><strong>Session cookies:</strong> short-lived cookies used for security and CSRF protection.</li>
          </ul>
          <p className="mt-2">
            We do not use third-party advertising or tracking cookies.
          </p>
        </Section>

        <Section title="6. Third-Party Providers">
          <div className="space-y-3">
            <div>
              <p><strong>Clerk</strong> — Authentication and user account management. Clerk stores your email address, name, and credentials. See <a href="https://clerk.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:no-underline">Clerk's Privacy Policy</a>.</p>
            </div>
            <div>
              <p><strong>OpenAI</strong> — AI text generation. Your lesson inputs and curriculum text are processed by OpenAI. See <a href="https://openai.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:no-underline">OpenAI's Privacy Policy</a>.</p>
            </div>
            <div>
              <p><strong>Replit</strong> — Infrastructure and hosting. Our servers run on Replit's platform. See <a href="https://replit.com/site/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:no-underline">Replit's Privacy Policy</a>.</p>
            </div>
            <div>
              <p><strong>Sentry</strong> (when enabled) — Error monitoring. If we enable Sentry, crash reports including non-sensitive technical context may be sent to Sentry. We configure Sentry to exclude lesson content from error breadcrumbs. See <a href="https://sentry.io/privacy/" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:no-underline">Sentry's Privacy Policy</a>.</p>
            </div>
          </div>
        </Section>

        <Section title="7. Data Retention and Deletion">
          <p>
            We retain your account data, resources, and curriculum files for as long as your account is active.
            If you delete a resource or curriculum entry, it is removed from our database within 30 days.
          </p>
          <p className="mt-2">
            If you wish to delete your account and all associated data, contact us at{' '}
            <a href="mailto:support@teachbud.app" className="text-primary underline hover:no-underline">support@teachbud.app</a>.
            We will process account deletion requests within 30 days and confirm when complete.
          </p>
          <p className="mt-2">
            Anonymized aggregate usage data (with no personal identifiers) may be retained indefinitely for product
            improvement purposes.
          </p>
        </Section>

        <Section title="8. Data Security">
          <p>
            We use industry-standard security practices including encrypted connections (HTTPS/TLS), secure token-based
            authentication, server-side user-ID isolation (each teacher can only access their own data), and
            parameterized database queries to prevent injection attacks.
          </p>
          <p className="mt-2">
            No system is completely secure. If you discover a security vulnerability, please report it to{' '}
            <a href="mailto:support@teachbud.app" className="text-primary underline hover:no-underline">support@teachbud.app</a>{' '}
            rather than disclosing it publicly.
          </p>
        </Section>

        <Section title="9. Student Data">
          <p>
            TeachBud accounts are intended for teachers, not students. We do not knowingly collect data from students.
            If you are a teacher, do not enter personally identifiable student information into TeachBud.
          </p>
          <p className="mt-2 font-medium text-amber-700">
            Important: TeachBud has not been formally reviewed for FERPA, COPPA, or state student-privacy law compliance
            as of this date. Do not use TeachBud to process or store student records until a formal compliance review has
            been completed. [Attorney review required before public launch.]
          </p>
        </Section>

        <Section title="10. Your Rights">
          <p>
            Depending on your location, you may have rights to access, correct, delete, or export your personal data.
            To exercise any of these rights, contact us at{' '}
            <a href="mailto:support@teachbud.app" className="text-primary underline hover:no-underline">support@teachbud.app</a>.
          </p>
          <p className="mt-2">
            We will respond to verified requests within 30 days. We may need to verify your identity before processing
            your request.
          </p>
        </Section>

        <Section title="11. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. When we do, we will update the "Last updated" date at
            the top of this page. During the private beta period, we will notify beta participants by email of any
            material changes before they take effect.
          </p>
        </Section>

        <Section title="12. Contact">
          <p>
            For privacy questions or data requests, contact us at:{' '}
            <a href="mailto:support@teachbud.app" className="text-primary underline hover:no-underline">support@teachbud.app</a>
          </p>
        </Section>

        <div className="pt-4 border-t border-slate-100 text-xs text-slate-400">
          <p>TeachBud · Private Beta · This policy is a draft pending attorney review.</p>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-base font-bold text-slate-900 mb-3">{title}</h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-3">
      <h3 className="text-sm font-semibold text-slate-800 mb-1.5">{title}</h3>
      {children}
    </div>
  );
}
